import { cn } from "@/lib/utils";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { Separator } from "./separator";

export type OptionType = {
  label: string;
  value: string;
  type?: string;
};

interface MultiSelectProps {
  options: OptionType[];
  selected: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
  className?: string;
  popOverClassName?: string;
  placeholder?: string;
  showSearch?: boolean;
  disabled?: boolean;
}

function MultiSelectAlt({
  options,
  selected,
  onChange,
  className,
  popOverClassName,
  placeholder,
  showSearch = false,
  ...props
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} {...props}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(className, "w-full justify-between")}
          onClick={() => setOpen(!open)}
          disabled={props.disabled}
        >
          <span className="text-muted-foreground">
            {placeholder || "Select"}{" "}
            {selected.length > 0 && `(${selected.length})`}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(popOverClassName, "w-full p-0")}>
        <Command className="min-w-36">
          {showSearch && <CommandInput placeholder="Search ..." />}
          <CommandList>
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {options.map((option) =>
                option.type === "divider" ? (
                  <Separator />
                ) : (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      onChange(
                        selected.includes(option.value)
                          ? selected.filter((item) => item !== option.value)
                          : [...selected, option.value],
                      );
                      setOpen(true);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selected.includes(option.value)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ),
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { MultiSelectAlt };
